import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Link, Drawer, Typography, Avatar, Stack, Chip } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
//
import NavConfig from './NavConfig';
import { useAuth } from "../../context/AuthContext";
import InfoCard from './InfoCard'
import { getUsageAccessInfo } from '../../services/helper';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.25),
  borderRadius: Number(theme.shape.borderRadius) * 1.6,
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 10px 24px rgba(23, 23, 23, 0.06)',
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const navConfig = NavConfig();
  const { currentUser } = useAuth();
  const accessInfo = getUsageAccessInfo(currentUser);
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ px: 2.5, pt: 3, pb: 2 }}>
        <Stack spacing={2}>
          <Logo />
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              color: 'common.white',
              bgcolor: 'primary.main',
              background: 'linear-gradient(135deg, #171717 0%, #0F3D28 100%)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main' }} />
              <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.72), fontWeight: 700 }}>
                Voice workspace
              </Typography>
            </Stack>
            <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 800 }}>
              Speak anywhere
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 2, mx: 2.5 }}>
        <Link underline="none" component={RouterLink} to="#">
          <AccountStyle>
            <Avatar src={currentUser?.photoURL} alt="photoURL" />
            <Box sx={{ ml: 1.5, minWidth: 0 }}>
              <Typography noWrap variant="subtitle2" sx={{ color: 'text.primary' }}>
                {currentUser?.displayName || 'Voice Typing'}
              </Typography>
              <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
                {currentUser?.email || 'Ready workspace'}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={accessInfo.paid ? 'Pro' : 'Free'}
              color={accessInfo.paid ? 'secondary' : 'default'}
              sx={{ ml: 'auto', fontWeight: 700 }}
            />
          </AccountStyle>
        </Link>
      </Box>

      <Typography variant="overline" sx={{ px: 3, color: 'text.secondary' }}>
        Workspace
      </Typography>

      <NavSection navConfig={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 3 }}>
        <InfoCard/>
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH, bgcolor: 'background.default' },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
